/* eslint-disable prettier/prettier */
import clsx from 'clsx';
import { useEffect, useState } from 'react';

import FormHelperText from '../FormHelperText';
import WarningIcon from '../Icon/WarningIcon';

import styles from './Select.module.css';
import config from 'config-base';
import { OptionValue, SelectBorderColor, SelectProps } from './types';

const mapBorderColor: { [key in SelectBorderColor]: string } = {
  inherit: styles.borderColor_inherit,
  primary: styles.borderColor_primary,
  secondary: styles.borderColor_secondary,
};

const Select = ({
  id,
  className,
  labelClassName,
  selectClasses,
  selectClassName,
  options,
  label,
  error,
  helperText,
  required,
  value,
  name,
  size = 'medium',
  color = 'inherit',
  variant = 'square',
  fullWidth,
  onChange,
  disableAutoComplete,
  autoComplete = 'on',
  disabled,
  emptyFirst,
  onBlur,
}: SelectProps) => {
  const [selectValue, setSelectValue] = useState<OptionValue>(value);
  const autoCompleteValue = disableAutoComplete
    ? Buffer.from(id || name)
        .toString('base64')
        .slice(0, 5)
    : autoComplete;
  const renderLabel = required ? `${label}*` : label;

  useEffect(() => {
    if (value && value === '') {
      return;
    } else if (value) {
      setSelectValue(value);
    }
  }, [value]);

  const handleChange = (event: React.ChangeEvent<{ name?: string; value: unknown }>): void => {
    setSelectValue(event.currentTarget.value as OptionValue);
    onChange && onChange(event.currentTarget.value as OptionValue);
  };

  const labelCLasses: string = clsx(
    styles.selectFieldLabel,
    styles.selectFieldTransition,
    labelClassName,
    {
      ['labelError']: error,
      [styles.selectFieldLabelDisabled]: disabled,
    },
    'fieldLabel',
  );
  const classes: string = clsx(
    styles.selectField,
    selectClassName,
    {
      [styles.smallSelect]: size === 'small',
      [styles.mediumSelect]: size === 'medium',
    },
    mapBorderColor[color],
    { 'w-full': fullWidth },
    {
      [styles.borderColor_primary]: error,
    },
  );

  return (
    <div className={clsx(styles.selectFieldTopContainer, className)}>
      {label && (
        <label className={labelCLasses} htmlFor={name}>
          {renderLabel}
        </label>
      )}
      <div
        className={clsx(
          styles.selectContainer,
          selectClasses,
          {
            roundedInput: variant === 'rounded',
            [styles.square]: variant === 'square',
            [styles.pyrSelect]: config.brandCode === '0',
            [styles.agdSelect]: config.brandCode === '2',
          },
          'inputContainer',
        )}
      >
        <select
          required={required}
          data-testid={id || name}
          id={id || name}
          name={name}
          value={selectValue}
          onChange={handleChange}
          className={classes}
          autoComplete={autoCompleteValue.toString()}
          aria-label={name}
          disabled={disabled}
          onBlur={onBlur}
        >
          {emptyFirst && <option key="first-option" hidden disabled selected></option>}
          {options.map((option: any) => {
            return (
              <option key={`option-${option.value}`} value={option.value}>
                {option.label}
              </option>
            );
          })}
        </select>
      </div>
      {error && (
        <FormHelperText className="flex items-center" error>
          <div className="flex">
            <WarningIcon disabled={disabled} />
          </div>
          <span className={clsx({ [styles.selectFieldLabelDisabled]: disabled })}>{helperText}</span>
        </FormHelperText>
      )}
    </div>
  );
};

export default Select;
